import type { ReactNode } from 'react';

import type { FieldType } from '@jira/polaris__domain-field/src/FieldType.types.tsx';
import type {
	FieldDescription,
	FieldKey,
} from '@jira/polaris__domain-field/src/FieldKey.types.tsx';
import type { IconProps } from '@atlaskit/icon/types';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createHook,
	createStateHook,
	type BoundActions,
	type HookFunction,
	type HookStateFunction,
} from '@atlassian/react-sweet-state';
import type { Actions } from './fieldActions.tsx';
import { FieldStore, createFieldHook } from './useFieldActions.tsx';
import type { State } from './FieldsUpdate.types.tsx';
import {
	createGetFieldDescription,
	createGetFieldEmoji,
	getFieldTypeIcon,
} from './createGetFieldByKey.tsx';
import { getFieldLabels, getNewFieldTypes, getFieldTypeByKey } from './getFieldArray.tsx';
import {
	createHigherLevelFieldHook,
	createHigherLevelFieldHook2,
} from './createHigherLevelFieldHook.tsx';

type BoundFieldActions = BoundActions<State, Actions>;

export const useFieldTypeIcon: (arg1: string, arg2: IconProps | undefined) => ReactNode =
	createHigherLevelFieldHook2<FieldKey, IconProps | undefined, ReactNode | null>(getFieldTypeIcon);

export const useFieldLabels: HookFunction<
	{
		[x: string]: string;
	},
	BoundFieldActions,
	void
> = createHook(FieldStore, {
	selector: getFieldLabels,
});

/**
 * Provides an object with mapping of field keys to their labels.
 */
export const useFieldLabelsMap: HookStateFunction<
	{
		[x: string]: string;
	},
	void
> = createStateHook(FieldStore, {
	selector: getFieldLabels,
});

export const useFieldTypes: HookFunction<
	{
		[x: string]: FieldType;
	},
	BoundFieldActions,
	void
> = createHook(FieldStore, {
	selector: getNewFieldTypes,
});

export const useFieldDescription: (arg1: string) => FieldDescription = createHigherLevelFieldHook<
	FieldKey,
	FieldDescription
>(createGetFieldDescription);

export const useFieldEmoji: (arg1: string) => string | undefined = createHigherLevelFieldHook<
	FieldKey,
	string | undefined
>(createGetFieldEmoji);

/** Map from field key to field type for all known fields. */
export const useFieldTypeByKey: HookStateFunction<Map<FieldKey, FieldType>, void> = createFieldHook(
	getFieldTypeByKey,
);
